import { useRef, useState, type FormEvent } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { shiftMonth } from "./callForm";
import { errorMessage, monthName, recordedLabel } from "./format";
import {
  draftFromViews,
  POSITION_CHOICES,
  toCreateRequest,
  togglePosition,
  type MarketFactor,
  type MarketViewDraft,
} from "./marketViewLogic";
import type { MarketView } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  factors: MarketFactor[];
  current: MarketView | null;
  previous: MarketView | null;
  canWrite: boolean;
  onSaved: (savedView: MarketView) => void;
  onClose: () => void;
}

export function MarketViewForm({
  month,
  resinFamilyCode,
  factors,
  current,
  previous,
  canWrite,
  onSaved,
  onClose,
}: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [draft, setDraft] = useState<MarketViewDraft>(() => draftFromViews(current, previous));
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const monthLabel = monthName(month);

  const saveMarketView = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving || !canWrite) return;
    setIsSaving(true);
    setStatusMessage("Saving…");
    try {
      const response = await apiFetchRef.current("consultant-cockpit/market-views", {
        method: "POST",
        body: JSON.stringify(toCreateRequest(draft, factors, resinFamilyCode, month)),
      });
      const savedView: MarketView = await response.json();
      setStatusMessage(`${resinFamilyCode} market view saved for ${monthLabel}. The Market Brief above is updated.`);
      onSaved(savedView);
    } catch (error) {
      setStatusMessage(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="mt-4">
      <form onSubmit={saveMarketView}>
        <h3 className="m-0 mb-3 text-[15px]">
          Your market view · {resinFamilyCode} · {monthLabel}
        </h3>
        <div className="mb-4 text-[13px]" role="status">
          {current ? (
            <>
              <b className="text-rs-green">Market view saved · {recordedLabel(current.recordedUtc)}</b>
              <p className="m-0 mt-1 text-rs-light-gray">
                Saving again replaces what the brief shows for {monthLabel}. Earlier saves stay in history.
              </p>
            </>
          ) : (
            <>
              <b className="text-white">
                {resinFamilyCode} · {monthLabel} · not saved yet
              </b>
              <p className="m-0 mt-1 text-rs-light-gray">
                {previous
                  ? `Started from your ${monthName(shiftMonth(month, -1))} positions. Change what moved.`
                  : "One market view per resin family per month."}
              </p>
            </>
          )}
        </div>

        <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray">
          Your view of the month in one line
          <input
            className="ctl"
            maxLength={160}
            placeholder="e.g. Buyers are gaining room"
            value={draft.summary}
            disabled={!canWrite}
            onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
          />
        </label>
        <p className="m-0 mt-2 text-[11.5px] text-rs-light-gray">
          For each factor: is it helping sellers push prices up, or buyers push them down? Click a choice again to
          clear it. All optional. This covers all of {resinFamilyCode}, not one resin code.
        </p>

        <div className="flex flex-col gap-2.5 mt-3">
          {factors.map((factor) => (
            <div
              key={factor.code}
              className="grid grid-cols-[170px_auto_minmax(0,1fr)] max-[900px]:grid-cols-1 gap-3 items-center border-t border-rs-jet pt-2.5"
            >
              <div>
                <b className="text-[13px] text-white">{factor.name}</b>
                {factor.hint && <small className="block text-[11.5px] text-rs-light-gray">{factor.hint}</small>}
              </div>
              <div className="seg w-fit m-0 [&_button]:px-2.5 [&_button]:py-1.5 [&_button]:text-xs" role="group" aria-label={factor.name}>
                {POSITION_CHOICES.map((choice) => (
                  <button
                    key={choice.score}
                    type="button"
                    aria-pressed={draft.positions[factor.code] === choice.score}
                    disabled={!canWrite}
                    onClick={() =>
                      setDraft({ ...draft, positions: togglePosition(draft.positions, factor.code, choice.score) })
                    }
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
              <input
                className="ctl"
                maxLength={200}
                placeholder="Why? (optional)"
                aria-label={`${factor.name} note`}
                value={draft.notes[factor.code] ?? ""}
                disabled={!canWrite}
                onChange={(event) =>
                  setDraft({ ...draft, notes: { ...draft.notes, [factor.code]: event.target.value } })
                }
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-6 pt-5 border-t border-rs-jet">
          <span className="flex-1 min-w-[200px] text-[12.5px] text-rs-light-gray" aria-live="polite">
            {statusMessage ??
              (canWrite
                ? "The Market Brief above updates when you save."
                : "Saving a market view needs the Uploader or Admin role.")}
          </span>
          <Btn variant="ghost" type="button" onClick={onClose}>
            Close
          </Btn>
          <Btn type="submit" disabled={!canWrite || isSaving}>
            <span className="material-symbols-sharp">save</span>Save market view
          </Btn>
        </div>
      </form>
    </Card>
  );
}
